import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";

import ActionButton from "../formInputs/buttons/ActionButton";
import DcsdDialog from "../modals/DcsdDialog";
import DifferentiatedStudentsTable from "./differentiation/DifferentiatedStudentsTable";
import DifferentiationSelectors from "./differentiation/DifferentiationSelectors";
import SetupDifferentiationModal from "./differentiation/SetupDifferentiationModal";
import StudentDiffModal from "./studentDifferentiation/StudentDiffModal";
import ElementaryProgressReportDao from "../../dao/ElementaryProgressReportDao";
import useAllEligibleStudents from "../../hooks/useAllEligibleStudents";
import useDifferentiatedStudents from "../../hooks/useDifferentiatedStudents";
import useDifferentiationSelection from "../../hooks/useDifferentiationSelection";
import useSubjectSelection from "../../hooks/useSubjectSelection";

import { GlobalContext } from "../contextProvider/ContextProvider";
import { getGradingCriteriaId } from "../../utils/teacher/InputRatingUtils";
import { useEnsureSchoolTemplatesForLocation } from "../contextProvider/ContextSetter";

import "../../styles/TeacherHome.scss";

/**
 * ManageDifferentiation component allows teachers to manage student differentiation settings.
 *
 * @component
 * @param {Object} props
 * @param {Function} dispatch
 * @param {Object} state
 * @returns {JSX.Element}
 */
const ManageDifferentiation = ({ dispatch, state, teacherGuid, schoolLevelTemplateDto: schoolLevelTemplateProp }) => {
    const { state: globalState } = useContext(GlobalContext);
    const {
        allLocationDtos,
        currentTimePeriodDto,
        schoolLevelTemplateDto: globalSchoolLevelTemplateDto,
        schoolTemplatesByLocation,
        schoolYearDto,
        token,
        userDetails
    } = globalState || {};
    const effectiveUserDetails = useMemo(
        () => (teacherGuid ? { ...userDetails, uid: teacherGuid } : userDetails),
        [teacherGuid, userDetails]
    );
    const { gradingProfiles, selectedEpr } = state;
    const ensureTemplatesForLocation = useEnsureSchoolTemplatesForLocation();
    const schoolLevelTemplateDto = schoolLevelTemplateProp || globalSchoolLevelTemplateDto;

    const gradingProfilesCurrentSem = (gradingProfiles || []).filter(
        (p) => Number(p.timePeriod) === Number(currentTimePeriodDto?.key)
    );

    const {
        selectedGrade,
        setSelectedGrade,
        selectedSchoolLocationKey,
        setSelectedSchoolLocationKey,
        sharedLocationKeys,
        gradesForSelect,
        combinedGrades,
        allSharedGroupOptions,
        sharedGradingProfileDtos
    } = useDifferentiationSelection({
        currentTimePeriodDto,
        gradingProfilesCurrentSem,
        schoolLevelTemplateDto,
        schoolYearDto,
        token,
        userDetails: effectiveUserDetails,
        dispatch
    });

    const schoolLevelTemplateOverride =
        selectedSchoolLocationKey && schoolTemplatesByLocation?.[selectedSchoolLocationKey]
            ? schoolTemplatesByLocation[selectedSchoolLocationKey]
            : null;

    const { subjectOptions, selectedTemplate, setSelectedTemplate } = useSubjectSelection({
        combinedGrades,
        selectedEpr,
        schoolLevelTemplateDto,
        schoolLevelTemplateOverride,
        incomingTemplateId: null,
        currentTimePeriodName: currentTimePeriodDto?.name,
        selectedGrade,
        view: "GROUPS",
        allSharedGroupOptions,
        gradingProfiles: gradingProfilesCurrentSem,
        sharedGradingProfileDtos
    });

    const { studentsWithDiff, loading, refetch } = useDifferentiatedStudents({
        selectedTemplate,
        selectedEpr,
        selectedGrade,
        gradingProfiles: gradingProfilesCurrentSem,
        allSharedGroupOptions,
        token,
        userDetails: effectiveUserDetails
    });

    const { allEligibleStudents } = useAllEligibleStudents({
        selectedGrade,
        selectedTemplate,
        gradingProfiles: gradingProfilesCurrentSem,
        allSharedGroupOptions,
        selectedSchoolLocationKey,
        schoolLevelTemplateDto,
        token,
        userDetails: effectiveUserDetails
    });

    const [selectedStudent, setSelectedStudent] = useState(null);
    const [showSetupModal, setShowSetupModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [open, setOpen] = useState("false");
    const [studentToReset, setStudentToReset] = useState(null);
    const [existingRatingsData, setExistingRatingsData] = useState([]);

    // Get grading profile for the selected grade (must be before modalStudent)
    const gradingProfile = useMemo(() => {
        // First try to get homeroom profile
        if (selectedEpr && selectedEpr.gradeName === selectedGrade) return selectedEpr;
        if (selectedGrade && gradingProfilesCurrentSem) {
            const homeroomProfile = gradingProfilesCurrentSem.find((p) => p.gradeName === selectedGrade);
            if (homeroomProfile) return homeroomProfile;
        }
        // If no homeroom profile and we have a selected student, use their sourceProfile
        if (selectedStudent?.sourceProfile) {
            return selectedStudent.sourceProfile;
        }
        return null;
    }, [selectedEpr, selectedGrade, gradingProfilesCurrentSem, selectedStudent]);

    // Ensure student has gradingProfileStudentId property (must be before callbacks)
    const modalStudent = useMemo(() => {
        if (!selectedStudent) return null;
        return {
            ...selectedStudent,
            gradingProfileStudentId: selectedStudent.gradingProfileStudentId || selectedStudent.key,
            gradingProfile: gradingProfile?.key
        };
    }, [selectedStudent, gradingProfile]);

    const saveExclusions = useCallback(
        async (items) => {
            if (!modalStudent || !selectedTemplate || !token || !existingRatingsData?.[0]) return;

            // Use the student's sourceProfile (their actual grading profile) not the teacher's
            const profileToUse = modalStudent.sourceProfile || gradingProfile;

            if (!profileToUse) return;

            const gradingCriteriaId = getGradingCriteriaId(
                modalStudent,
                null,
                profileToUse,
                selectedTemplate,
                "HOMEROOM"
            );

            const existingExcluded = new Set(
                (existingRatingsData[0].contentRatings || [])
                    .filter((r) => r.rating === "EXCLUDED")
                    .map((r) => r.templateContentId)
            );

            const newExcluded = new Set(items.map((item) => item.templateContentId));

            const contentRatings = [];

            // Add new exclusions
            items.forEach((item) => {
                if (!existingExcluded.has(item.templateContentId)) {
                    const existing = existingRatingsData[0].contentRatings.find(
                        (r) => r.templateContentId === item.templateContentId
                    );
                    contentRatings.push({
                        key: existing?.key ?? null,
                        gradingProfileStudentId: Number(modalStudent.gradingProfileStudentId),
                        gradingCriteria: Number(gradingCriteriaId),
                        templateContentId: item.templateContentId,
                        contentType: item.contentType,
                        rating: "EXCLUDED",
                        comment: null,
                        personalizedLearningPlan: false,
                        lastRatedGuid: userDetails?.uid
                    });
                }
            });

            // Remove exclusions that are no longer selected
            existingExcluded.forEach((templateContentId) => {
                if (!newExcluded.has(templateContentId)) {
                    const existing = existingRatingsData[0].contentRatings.find(
                        (r) => r.templateContentId === templateContentId
                    );

                    if (existing) {
                        contentRatings.push({
                            key: existing.key,
                            gradingProfileStudentId: modalStudent.gradingProfileStudentId,
                            gradingCriteria: Number(gradingCriteriaId),
                            templateContentId,
                            contentType: existing.contentType,
                            rating: null,
                            comment: null,
                            personalizedLearningPlan: false,
                            lastRatedGuid: userDetails?.uid
                        });
                    }
                }
            });

            if (contentRatings.length === 0) return;

            try {
                const res = await ElementaryProgressReportDao({
                    action: "updateStudentRatings",
                    gradingCriteriaId,
                    gradingProfileStudentId: modalStudent.gradingProfileStudentId,
                    data: { contentRatings },
                    token
                });
                const updated = res?.data?.payload || null;
                if (updated) {
                    setExistingRatingsData([updated]);
                }
                toast.success("Excluded content items saved successfully.");
                return true;
            } catch (error) {
                console.error("Failed to save exclusions:", error);
                toast.error("Failed to save content item exclusions. Please try again later.");
                return false;
            }
        },
        [modalStudent, gradingProfile, selectedTemplate, token, userDetails?.uid, existingRatingsData]
    );

    // Build studentProfileData for the modal
    // Use the student's sourceProfile (their actual grading profile) not the teacher's
    const studentProfileData = useMemo(() => {
        if (!selectedStudent) return null;
        const profileToUse = selectedStudent.sourceProfile || gradingProfile;
        if (!profileToUse) return null;
        return {
            gradingProfileId: profileToUse.key,
            gradeName: profileToUse.gradeName,
            studentNumber: selectedStudent.studentNumber,
            sectionId: selectedStudent.sectionId
        };
    }, [selectedStudent, gradingProfile]);

    const handleEdit = (student) => {
        setSelectedStudent(student);
        setShowEditModal(true);
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        refetch();
    };

    // Fetch existing ratings when student is selected
    useEffect(() => {
        if (!showEditModal || !modalStudent || !selectedTemplate || !token) {
            return;
        }

        // Use the student's sourceProfile (their actual grading profile) not the teacher's
        const profileToUse = modalStudent.sourceProfile || gradingProfile;

        if (!profileToUse) return;

        const gradingCriteriaId = getGradingCriteriaId(modalStudent, null, profileToUse, selectedTemplate, "HOMEROOM");

        ElementaryProgressReportDao({
            action: "getStudentRatings",
            gradingProfileStudentId: modalStudent.gradingProfileStudentId,
            gradingCriteriaId,
            token
        })
            .then((res) => {
                const payload = res?.data?.payload || null;
                setExistingRatingsData(payload ? [payload] : []);
            })
            .catch((error) => {
                console.error("Failed to fetch existing ratings:", error);
                setExistingRatingsData([]);
            });
    }, [showEditModal, modalStudent, gradingProfile, selectedTemplate, token]);

    const handleReset = (student) => {
        setStudentToReset(student);
        setOpen("confirm-reset-differentiation");
    };

    const confirmReset = async () => {
        if (!studentToReset || !selectedTemplate || !token) return;

        // Use the student's sourceProfile to ensure we get the correct grading profile
        const profileToUse = studentToReset.sourceProfile || gradingProfile;
        if (!profileToUse) return;

        const gradingCriteriaId = getGradingCriteriaId(
            studentToReset,
            null,
            profileToUse,
            selectedTemplate,
            "HOMEROOM"
        );

        const payload = [
            {
                gradingProfileStudentIds: [studentToReset.gradingProfileStudentId || studentToReset.key],
                gradingCriteria: Number(gradingCriteriaId)
            }
        ];

        try {
            await ElementaryProgressReportDao({
                action: "resetStudentDifferentiation",
                data: payload,
                token
            });
            toast.success("Differentiation reset successfully for the selected student.");
            refetch();
        } catch (error) {
            console.error("Failed to reset differentiation:", error);
            toast.error("Failed to reset differentiation for the selected student. Please try again later.");
        } finally {
            setOpen("false");
            setStudentToReset(null);
        }
    };

    const handleSetupOthers = () => {
        setShowSetupModal(true);
    };

    const handleSetupComplete = () => {
        refetch();
    };

    // Ensure templates are loaded for selected school
    useEffect(() => {
        if (selectedSchoolLocationKey) {
            ensureTemplatesForLocation({ locationKey: selectedSchoolLocationKey });
        }
    }, [selectedSchoolLocationKey, ensureTemplatesForLocation]);

    const showSchoolSelect = sharedLocationKeys.length > 1;
    const showGradeSelect = gradesForSelect.length >= 1 && (!showSchoolSelect || Boolean(selectedSchoolLocationKey));
    const showSubjectSelect =
        Boolean(selectedGrade || (selectedEpr && !showGradeSelect)) &&
        (!showSchoolSelect || Boolean(selectedSchoolLocationKey));

    // Check if there are no grading profiles and no shared groups with edit permissions
    const hasNoContent = gradingProfilesCurrentSem.length === 0 && gradesForSelect.length === 0;

    return (
        <div className="right-side-wrapper">
            <div className="right-page-title">
                <h4>
                    Manage Differentiation{" "}
                    {selectedEpr && (
                        <span>
                            &ndash; {selectedEpr.gradeName === "K" ? "Kindergarten" : `${selectedEpr.gradeName} Grade`}
                        </span>
                    )}
                </h4>
                <h5>
                    <span style={{ fontWeight: "700" }}>{currentTimePeriodDto?.name}</span> | {schoolYearDto.name}
                </h5>
            </div>
            <hr />
            <DifferentiationSelectors
                allLocationDtos={allLocationDtos}
                dispatch={dispatch}
                gradesForSelect={gradesForSelect}
                gradingProfilesCurrentSem={gradingProfilesCurrentSem}
                selectedGrade={selectedGrade}
                selectedSchoolLocationKey={selectedSchoolLocationKey}
                selectedTemplate={selectedTemplate}
                setSelectedGrade={setSelectedGrade}
                setSelectedSchoolLocationKey={setSelectedSchoolLocationKey}
                setSelectedTemplate={setSelectedTemplate}
                sharedLocationKeys={sharedLocationKeys}
                showGradeSelect={showGradeSelect}
                showSchoolSelect={showSchoolSelect}
                showSubjectSelect={showSubjectSelect}
                subjectOptions={subjectOptions}
            />
            {hasNoContent ? (
                <div>
                    <p>
                        Differentiation management is unavailable until you create your homeroom Report Card <b>OR</b>{" "}
                        your colleague(s) share a group/class with you.
                    </p>
                </div>
            ) : (
                selectedTemplate &&
                !loading && (
                    <>
                        <DifferentiatedStudentsTable
                            students={studentsWithDiff}
                            onEdit={handleEdit}
                            onReset={handleReset}
                            onSetupOthers={handleSetupOthers}
                        />
                        <SetupDifferentiationModal
                            show={showSetupModal}
                            onHide={() => {
                                setShowSetupModal(false);
                                refetch();
                            }}
                            allStudents={allEligibleStudents.all}
                            studentsWithDiff={studentsWithDiff}
                            selectedTemplate={selectedTemplate}
                            selectedGrade={selectedGrade}
                            gradingProfile={gradingProfile}
                            onComplete={handleSetupComplete}
                        />
                        {modalStudent && gradingProfile && (
                            <StudentDiffModal
                                show={showEditModal}
                                setShow={handleCloseEditModal}
                                student={modalStudent}
                                selectedTemplate={selectedTemplate}
                                studentProfileData={{
                                    ...studentProfileData,
                                    gradeName: (selectedStudent?.sourceProfile || gradingProfile).gradeName
                                }}
                                existingRatingsData={existingRatingsData}
                                onApplyExclusions={async (items) => {
                                    const success = await saveExclusions(items);
                                    if (success) {
                                        return "SWITCH_TO_DIFF";
                                    }
                                }}
                                onSaved={handleCloseEditModal}
                            />
                        )}
                        <DcsdDialog
                            actions={
                                <div className="modal-btn-group">
                                    <ActionButton
                                        className="action-button-cancel"
                                        label="Cancel"
                                        onClick={() => {
                                            setOpen("false");
                                            setStudentToReset(null);
                                        }}
                                    />
                                    <ActionButton
                                        className="action-button-reg"
                                        label="Confirm and Reset"
                                        onClick={confirmReset}
                                    />
                                </div>
                            }
                            ariaLabel="Reset Differentiation Confirmation Dialog"
                            hasCloseX={false}
                            id="confirm-reset-differentiation"
                            open={open}
                            title="Reset Differentiation"
                        >
                            <div>
                                This action will remove all previously added differentiated content with and/or without
                                a rating. Additionally, all excluded content items will be restored from the grade-level
                                template. This action cannot be undone.
                            </div>
                        </DcsdDialog>
                    </>
                )
            )}
        </div>
    );
};

ManageDifferentiation.propTypes = {
    dispatch: PropTypes.func.isRequired,
    state: PropTypes.object.isRequired,
    teacherGuid: PropTypes.string,
    schoolLevelTemplateDto: PropTypes.object
};

export default ManageDifferentiation;
